import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { Box, Typography, Alert, Button, Skeleton } from '@mui/material'
import { Person as PersonIcon } from '@mui/icons-material'
import { userAtom } from '../state/auth'
import { rememberedClientIdAtom, isJobSearchEligible } from '../state/clients'
import { clientsApi, Client } from '../services/api'
import { isAdminOrManager } from '../utils/roles'
import ClientPicker from '../components/common/ClientPicker'
import PageHeader from '../components/common/PageHeader'
import NetworkingTab from '../components/client-detail/NetworkingTab'

/**
 * Staff Networking page. Networking is its own activity rather than part of an
 * application — connecting with someone is not the same as having a job — so it
 * gets its own nav item instead of living inside the applications table or on a
 * client-record tab.
 *
 * The page is client-scoped: the picker chooses whose network is on screen, and
 * the selected client is re-read in full (list-clients is not the source of
 * truth for the nested dreamJob target companies that drive the
 * dream/target/back-up/wildcard tiering, nor for the LinkedIn-coverage counters).
 */
export default function Networking() {
  const [user] = useAtom(userAtom)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [clients, setClients] = useState<Client[]>([])
  const [clientsLoading, setClientsLoading] = useState(true)
  const [clientsError, setClientsError] = useState<string | null>(null)
  // The client now lives in the route (/clients/:id/networking) rather than
  // local state, so the URL is shareable and survives navigation.
  const { id: routeClientId } = useParams<{ id: string }>()
  const selectedClientId = routeClientId ?? null
  const [, setRememberedClientId] = useAtom(rememberedClientIdAtom)

  // The full record for the selected client, read separately (see above).
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [selectedClientLoading, setSelectedClientLoading] = useState(false)
  const [selectedClientError, setSelectedClientError] = useState<string | null>(null)
  // Bumped by the error state's Retry, which has to re-run the read for the
  // client that is already selected.
  const [clientReadToken, setClientReadToken] = useState(0)

  const isManager = isAdminOrManager(user) || user?.role === 'client_success'

  const loadClients = async () => {
    setClientsLoading(true)
    setClientsError(null)
    try {
      const result = await clientsApi.list({ all: true })
      if (!Array.isArray(result)) {
        console.error('Unexpected response shape from list-clients:', result)
        setClients([])
        setClientsError('Could not load clients — unexpected response from server.')
        return
      }
      setClients(result)
    } catch (err) {
      console.error('Failed to load clients for the networking client switcher:', err)
      setClients([])
      setClientsError('Could not load clients. Please try again.')
    } finally {
      setClientsLoading(false)
    }
  }

  useEffect(() => {
    loadClients()
  }, [])

  // clientIds the logged-in staff member is assigned to (coach / clientSuccess /
  // assignedTo). Drives the "My Clients" group on the picker.
  const assignedClientIds = useMemo(() => {
    const email = user?.email
    if (!email) return new Set<string>()
    return new Set(
      clients
        .filter((c) => c.coach === email || c.clientSuccess === email || c.assignedTo === email)
        .map((c) => c.id)
    )
  }, [clients, user?.email])

  // Every client is offered here, not just market-ready ones: a LinkedIn import
  // lands during intake, long before a client is on the market, and this page is
  // the only staff way into a client's network. Managers get their own clients
  // grouped ahead of everyone else (the picker's groupBy needs them sorted
  // mine-first); non-managers stay scoped to their assigned clients, falling
  // back to the full list when they have none.
  const clientOptions = useMemo(() => {
    const base =
      !isManager && assignedClientIds.size > 0
        ? clients.filter((c) => assignedClientIds.has(c.id))
        : clients
    return [...base].sort((a, b) => {
      if (isManager) {
        const aMine = assignedClientIds.has(a.id) ? 0 : 1
        const bMine = assignedClientIds.has(b.id) ? 0 : 1
        if (aMine !== bMine) return aMine - bMine
      }
      return (a.name || a.email || '').localeCompare(b.name || b.email || '')
    })
  }, [clients, isManager, assignedClientIds])

  useEffect(() => {
    if (!selectedClientId) {
      setSelectedClient(null)
      setSelectedClientError(null)
      return
    }
    let cancelled = false
    setSelectedClientLoading(true)
    setSelectedClientError(null)
    clientsApi
      .get(selectedClientId)
      .then((result) => {
        if (cancelled) return
        setSelectedClient(result)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load the selected client:', err)
        setSelectedClient(null)
        setSelectedClientError('Could not load this client. Please try again.')
      })
      .finally(() => {
        if (!cancelled) setSelectedClientLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedClientId, clientReadToken])

  // Remember this client as the last one the Job Search flow opened, so the
  // flat /opportunities and /networking links (and the sidebar) know where
  // to send you next time.
  //
  // Only remembered when they are market-ready, even though this page itself
  // serves every client: the memory is shared with the job applications
  // tracker, which does gate on it, so remembering a pre-market client here
  // would send the sidebar somewhere that refuses to open.
  useEffect(() => {
    if (selectedClientId && isJobSearchEligible(selectedClient)) {
      setRememberedClientId(selectedClientId)
    }
  }, [selectedClientId, selectedClient, setRememberedClientId])

  // Falls back to the full client list (not just clientOptions) because the
  // id now comes from the URL — it can legitimately name a client outside a
  // non-manager's assigned/option set, and the picker should still show who
  // that is rather than rendering blank.
  const pickerValue =
    clientOptions.find((c) => c.id === selectedClientId) ||
    clients.find((c) => c.id === selectedClientId) ||
    null

  // Re-surfaced from the client-record Networking tab, which used to carry this
  // as a warning badge on the tab itself. An import that produced almost
  // nothing is a real signal — usually a partial LinkedIn export — and the tab
  // it lived on is gone, so it is stated here in full instead.
  const importedCount = selectedClient?.networkingContactCount ?? 0
  const lowCoverage = !!selectedClient?.hasLinkedinImport && importedCount < 5

  const renderContent = () => {
    if (clientsLoading) {
      return (
        <Box>
          <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={52} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={52} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={52} />
        </Box>
      )
    }

    if (clientsError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadClients}>
              Retry
            </Button>
          }
        >
          {clientsError}
        </Alert>
      )
    }

    if (clientOptions.length === 0) {
      return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No clients available yet. Convert a lead into a client to start tracking their network.
          </Typography>
        </Box>
      )
    }

    if (!selectedClientId) {
      return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Select a client to view their network.
          </Typography>
        </Box>
      )
    }

    if (selectedClientError) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setClientReadToken((t) => t + 1)}>
              Retry
            </Button>
          }
        >
          {selectedClientError}
        </Alert>
      )
    }

    if (selectedClientLoading || !selectedClient) {
      return (
        <Box>
          <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={52} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={52} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={52} />
        </Box>
      )
    }

    // Deliberately NOT gated on market_ready, unlike the job applications
    // tracker. A client's LinkedIn import lands during intake, long before they
    // are on the market, and this is the only staff way into their network — so
    // restricting it here would strand that work. See the clientOptions comment
    // above, which is why every client is offered in the picker too.

    return <NetworkingTab client={selectedClient} />
  }

  // Old bookmarks (e.g. Client Health's per-client "View" button) named the
  // client via ?clientId= on the flat /networking path. Now that the id lives
  // in the route, honour a leftover query param by redirecting to the scoped
  // URL for it rather than silently ignoring it and landing on the wrong client.
  const requestedClientIdParam = searchParams.get('clientId')
  if (requestedClientIdParam) {
    return <Navigate to={`/clients/${requestedClientIdParam}/networking`} replace />
  }

  return (
    <Box>
      <Box sx={{ mb: { xs: 1, sm: 2 } }}>
        <PageHeader
          title="Side Door"
          scope={
        /* Sits beside the title, and is what names the client on screen — the
            top nav is navigation alone now. A separate name label next to the
            title only repeated what this field already reads.

            The only client selector on this page at every width, since the
            toolbar no longer carries one.

            Note this picker offers every client while the job applications one
            offers only market-ready clients — deliberate, because networking
            serves clients from intake onward (see clientOptions above). */
            <Box sx={{ flexShrink: 0 }}>
              <ClientPicker
                options={clientOptions}
                value={pickerValue}
                onChange={(c) => {
                  // Clearing the picker has no scoped URL to land on, so it's a
                  // no-op — the field snaps back to the route's client on the
                  // next render, same as any other controlled Autocomplete.
                  if (c) navigate(`/clients/${c.id}/networking`)
                }}
                isManager={isManager}
                assignedClientIds={assignedClientIds}
              />
            </Box>
          }
          actions={
            /* The way back to the client this network belongs to. Without it the
               page is a dead end: the record links here, and the only route back
               was the browser's back button. */
            selectedClientId ? (
              <Button
                startIcon={<PersonIcon />}
                onClick={() => navigate(`/clients/${selectedClientId}`)}
                sx={{ minHeight: 44 }}
              >
                Client record
              </Button>
            ) : undefined
          }
        />
      </Box>

      {lowCoverage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Low network coverage — only {importedCount} connection
          {importedCount === 1 ? '' : 's'} came through from this client&rsquo;s LinkedIn import.
          Ask them for a full Connections export, or add key contacts by hand.
        </Alert>
      )}

      {renderContent()}
    </Box>
  )
}
